import { NextSeo } from "next-seo";
import { useRouter } from "next/router";
import React, { type ReactElement, useEffect, useState } from "react";
import fetchJson, { type FetchError } from "~/lib/fetchJson";
import { type User } from "~/pages/api/auth/user";
import useUser from "~/lib/useUser";

const AppLoginCallback = () => {
  const { query, replace, push } = useRouter();
  const { error } = query;
  const token = query.token as string;
  const refreshToken = query.refreshToken as string;
  const [allowedReturnToken, setAllowedReturnToken] = useState<boolean>(false);
  const { mutateUser } = useUser();

  useEffect(() => {
    if (!token || !refreshToken) return;
    fetchJson("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, refreshToken }),
    })
      .then(async (user) => {
        const tempUser = user as User;
        await mutateUser(tempUser);

        if (tempUser?.isLoggedIn && !tempUser?.userConsent) {
          const url =
            window.sessionStorage &&
            sessionStorage.getItem("terms-mode") === "full"
              ? `/terms?redirect=/app/callback%3Ftoken=${token}%26refreshToken=${refreshToken}`
              : `/terms-compact?redirect=/app/callback%3Ftoken=${token}%26refreshToken=${refreshToken}`;
          await replace(url);
        } else {
          setAllowedReturnToken(true);
        }
      })
      .catch(async (error: FetchError) => {
        await mutateUser(undefined);
        console.error("An unexpected error happened:", error);
      });
  }, [token, mutateUser, push, refreshToken, error, replace]);

  if (error) {
    return JSON.stringify({
      error: error,
    });
  }

  if (allowedReturnToken && token) {
    return JSON.stringify({
      accessToken: token,
      refreshToken,
    });
  }

  return null;
};

AppLoginCallback.getLayout = function getLayout(page: ReactElement) {
  return (
    <>
      <NextSeo title="Login Callback" nofollow={true} noindex={true} />
      {page}
    </>
  );
};

export default AppLoginCallback;
